import RadioButtonCheckedSharpIcon from '@mui/icons-material/RadioButtonCheckedSharp';
import RadioButtonUncheckedSharpIcon from '@mui/icons-material/RadioButtonUncheckedSharp';
import { Button, Card, CardHeader, CardMedia, CircularProgress, Container, Grid, MenuItem, Pagination, SelectChangeEvent } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as GeometryHelper from '../api/geometry/geometryHelper';
import { IGetGeometryPreviewResponse } from '../api/geometry/iApiResponse';
import { IPaginatedResponse } from '../api/httpClient';
import * as ProjectHelper from '../api/project/projectHelper';
import { route } from '../route/Index';
import DropDown from '../shared/component/DropDown';
import { EUnitOfMeasure } from '../shared/enum';
import isFulfilledProject from '../shared/hoc/isFulfilledProject';
import { IProjectInfo } from '../shared/iProject';
import variable from '../shared/style/variable';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { selectProject, setProject, setProjectInfo } from '../store/project/projectSlice';
import { selectUser } from '../store/user/userSlice';
import { cleanResult } from '../store/result/resultSlice';
import { cleanEngine } from '../store/engine/engineSlice';
import { cleanMap } from '../store/map/mapSlice';
import { cleanAlert } from '../store/alert/alertSlice';

interface IGeometryProps {}

const GeometryContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
    margin-top: 1rem;
`;

const Geometry = (props: IGeometryProps) => {
    let pageSize = 4;

    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [geometry, setGeometry] = React.useState<IPaginatedResponse<IGetGeometryPreviewResponse>>();
    const [geometryId, setGeometryId] = React.useState<number | undefined>(
        project.data?.project!.geometryId ? project.data.project.geometryId : project.data?.geometry ? project.data?.geometry.baseGeometryId : undefined,
    );
    const [unitOfMeasure, setUnitOfMeasure] = React.useState<EUnitOfMeasure>(project.data?.project!.unitOfMeasure ? project.data.project.unitOfMeasure! : EUnitOfMeasure.Metric);
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);

    React.useEffect(() => {
        const fetchGeometry = async (page: number) => {
            setIsLoading(true);
            let res = await GeometryHelper.getGeometryPreviewAsync(page, pageSize, undefined, user.data!.isAdmin);
            setGeometry(res.data);
            setGeometryId(geometryId ? geometryId : res.data.value[0].id);
            setIsLoading(false);
        };
        fetchGeometry(pageNumber);
    }, [pageNumber]);

    const handleClickNext = () => {
        setIsLoading(true);
        if (project.data?.project?.id) {
            Swal.fire({
                title: t('areYouSure'),
                text: t('warningReInizializeProject'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: variable.green,
                cancelButtonColor: variable.red,
                confirmButtonText: t('yes'),
                cancelButtonText: t('cancel'),
            }).then(async result => {
                if (result.isConfirmed) {
                    let engineIds: number[] = project.data!.engines!.map(x => x.baseEngineId!);
                    let payload: IProjectInfo = {
                        ...project.data!.project,
                        authorEmail: user.data?.email,
                        authorName: user.data?.first_Name,
                        authorSurname: user.data?.last_Name,
                        geometryId,
                        unitOfMeasure,
                        scenarioId: project.data?.scenarios![0].baseScenarioId,
                        engineIds,
                    };
                    let res = await ProjectHelper.createProjectAsync(payload);
                    if (res.status == 200) {
                        dispatch(cleanResult());
                        dispatch(cleanEngine());
                        dispatch(cleanMap());
                        dispatch(cleanAlert());
                        dispatch(setProject(res.data));
                        navigate(route.design.replace(':projectId', res.data.project!.id!.toString()));
                    }
                }
            });
        } else {
            dispatch(setProjectInfo({ geometryId, unitOfMeasure }));
            navigate(route.scenario);
        }
        setIsLoading(false);
    };

    const handleChangeUnitOfMeasure = (event: SelectChangeEvent<any>) => setUnitOfMeasure(parseInt(event.target.value));

    const handleChangeGeometry = (geometryId: number) => setGeometryId(geometryId);

    return (
        <GeometryContainer maxWidth='md'>
            <Stack height={'100%'} direction='column' spacing={4}>
                <Stack direction={'row'} justifyContent='space-between' alignItems={'end'}>
                    <Typography variant='h1' gutterBottom>
                        {t('geometry')}
                    </Typography>
                    <DropDown inputLabel={t('unitOfMeasure')} defaultValue={EUnitOfMeasure.Metric.toString()} id='unitOfMeasure' onChange={handleChangeUnitOfMeasure}>
                        <MenuItem value={EUnitOfMeasure.Metric}> {t('metric')}</MenuItem>
                        <MenuItem value={EUnitOfMeasure.Imperial}> {t('imperial')}</MenuItem>
                    </DropDown>
                </Stack>
                {isLoading && <CircularProgress />}
                {!isLoading && geometry?.value?.length! > 0 && (
                    <Stack flexGrow={1} direction={'column'} justifyContent='space-between'>
                        <Grid container spacing={5}>
                            {geometry!.value.map(x => (
                                <Grid key={x.id} item md={3} sm={6} xs={12}>
                                    <Card style={{ height: '220px', width: '180px', margin: 'auto' }} variant={geometryId == x.id ? 'selected' : 'standard'} onClick={() => handleChangeGeometry(x.id)}>
                                        <Stack height={'30%'} direction={'row'} padding={'0rem 1rem'} justifyContent={'space-between'} alignItems={'center'}>
                                            <CardHeader style={{ padding: 0 }} title={<Typography>{x.name}</Typography>} />
                                            {geometryId == x.id ? <RadioButtonCheckedSharpIcon style={{ fill: variable.green }} /> : <RadioButtonUncheckedSharpIcon style={{ fill: variable.green }} />}
                                        </Stack>
                                        <CardMedia height={'70%'} component='img' image={x.imageUrl} alt={x.name} />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {geometry!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination showFirstButton showLastButton page={geometry!.pageNumber} count={geometry!.pageCount} onChange={(e, value) => setpageNumber(value)} />
                            </Stack>
                        )}
                    </Stack>
                )}
                <Stack alignItems='end'>
                    <Button variant='orange' onClick={handleClickNext}>
                        {t('next')}
                    </Button>
                </Stack>
            </Stack>
        </GeometryContainer>
    );
};

export default isFulfilledProject(Geometry);
